import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  Group,
  OverflowGroupClassification,
  TournamentFormat,
} from '@/types/tournament.model';
import { TVGroupTable } from './TVGroupTable';
import {
  TV_GROUP_CARD_MIN_HEIGHT_PX,
  TV_GROUP_CAROUSEL_DOTS_RESERVE_PX,
  TV_GROUP_CAROUSEL_GAP_PX,
  TV_GROUP_CAROUSEL_INTERVAL_MS,
  TV_GROUP_CAROUSEL_TRACK_MS,
  TV_GROUPS_PER_PAGE,
  TV_PALETTE,
} from './tv-mode.constants';
import { useTVScale } from './TVScaleContext';
import { groupLabel } from '@/utils/fixture-utils';

interface Props {
  groups: Group[];
  accent: string;
  classifiedPerGroup?: number;
  overflowGroupClassification?: OverflowGroupClassification;
  format?: TournamentFormat;
}

function fitWindowSize(
  containerHeight: number,
  groupCount: number,
  reserveForDots: boolean,
  minCard: number,
  gap: number,
  dotsReserve: number,
): number {
  const n = groupCount;
  if (n === 0) return 0;

  const cell = minCard + gap;

  let inner = containerHeight;
  if (reserveForDots) inner -= dotsReserve;

  if (inner <= 0 || !Number.isFinite(inner)) {
    return Math.min(n, TV_GROUPS_PER_PAGE);
  }

  const w = Math.floor((inner + gap) / cell);
  return Math.max(1, Math.min(n, w));
}

function computeWindowSize(
  containerHeight: number,
  groupCount: number,
  minCard: number,
  gap: number,
  dotsReserve: number,
): number {
  const n = groupCount;
  if (n === 0) return 0;

  if (containerHeight <= 0 || !Number.isFinite(containerHeight)) {
    return Math.min(n, TV_GROUPS_PER_PAGE);
  }

  let w = fitWindowSize(
    containerHeight,
    n,
    false,
    minCard,
    gap,
    dotsReserve,
  );
  if (n > w) {
    w = fitWindowSize(containerHeight, n, true, minCard, gap, dotsReserve);
  }
  return w;
}

/** Páginas con máximo solapamiento: paso (w-1) → A,B,C luego C,D,E */
function buildPageOffsets(maxStart: number, pageStep: number): number[] {
  if (maxStart <= 0) return [0];
  const out: number[] = [];
  for (let o = 0; o <= maxStart; o += pageStep) {
    out.push(o);
  }
  if (out[out.length - 1] !== maxStart) {
    out.push(maxStart);
  }
  return out;
}

export const TVGroupCarousel = ({
  groups,
  accent,
  classifiedPerGroup,
  overflowGroupClassification,
  format,
}: Props) => {
  const { s } = useTVScale();
  const n = groups.length;
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);
  const [viewportH, setViewportH] = useState(0);
  const [trackTransitionOn, setTrackTransitionOn] = useState(false);

  const minCard = s(TV_GROUP_CARD_MIN_HEIGHT_PX);
  const carouselGap = s(TV_GROUP_CAROUSEL_GAP_PX);
  const dotsReserve = s(TV_GROUP_CAROUSEL_DOTS_RESERVE_PX);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const update = () => {
      setContainerHeight(el.getBoundingClientRect().height);
    };

    update();
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setContainerHeight(h);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const h = entries[0]?.contentRect.height ?? 0;
      setViewportH(h);
    });
    ro.observe(el);
    setViewportH(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, []);

  /** Primera pintura sin transición; luego animar translateY como ruleta */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setTrackTransitionOn(true);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const windowSize = useMemo(
    () =>
      computeWindowSize(containerHeight, n, minCard, carouselGap, dotsReserve),
    [containerHeight, n, minCard, carouselGap, dotsReserve],
  );

  const maxStart = Math.max(0, n - windowSize);
  const pageStep = windowSize <= 1 ? 1 : windowSize - 1;
  const pageOffsets = useMemo(
    () => buildPageOffsets(maxStart, pageStep),
    [maxStart, pageStep],
  );
  const pageCount = pageOffsets.length;

  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (pageCount <= 1) return;
    const id = setInterval(() => {
      setSlide((s) => (s + 1) % pageCount);
    }, TV_GROUP_CAROUSEL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [pageCount]);

  useEffect(() => {
    if (slide >= pageCount) setSlide(0);
  }, [slide, pageCount]);

  const startOffset = pageOffsets[Math.min(slide, pageCount - 1)] ?? 0;

  const gap = carouselGap;
  const rowHeight =
    windowSize > 0 && viewportH > 0
      ? (viewportH - (windowSize - 1) * gap) / windowSize
      : minCard;
  const slotHeight = rowHeight + gap;
  const translateY = -startOffset * slotHeight;

  const trackEase = 'cubic-bezier(0.22, 1, 0.36, 1)';

  if (groups.length === 0) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TV_PALETTE.textMuted,
          fontSize: s(13),
        }}
      >
        Sin grupos disponibles todavía
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <div
        ref={viewportRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap,
            transform: `translate3d(0, ${translateY}px, 0)`,
            transition: trackTransitionOn
              ? `transform ${TV_GROUP_CAROUSEL_TRACK_MS}ms ${trackEase}`
              : 'none',
            willChange: 'transform',
          }}
        >
          {groups.map((g, globalIdx) => {
            const groupName = groupLabel(globalIdx);
            return (
              <div
                key={`g-${globalIdx}`}
                style={{
                  height: rowHeight,
                  flexShrink: 0,
                  background: TV_PALETTE.card,
                  borderRadius: 8,
                  padding: `${s(12)}px ${s(14)}px`,
                  border: `1px solid ${TV_PALETTE.border}`,
                  boxShadow: '0 1px 6px rgba(2,51,115,0.06)',
                  overflow: 'auto',
                  boxSizing: 'border-box',
                }}
              >
                <TVGroupTable
                  group={g}
                  groupName={groupName}
                  accent={accent}
                  classifiedPerGroup={classifiedPerGroup}
                  overflowGroupClassification={overflowGroupClassification}
                  format={format}
                />
              </div>
            );
          })}
        </div>
      </div>

      {pageCount > 1 && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: s(6),
            marginTop: s(12),
            flexShrink: 0,
          }}
        >
          {pageOffsets.map((offset, i) => (
            <button
              key={offset}
              type="button"
              onClick={() => setSlide(i)}
              style={{
                width: i === slide ? s(20) : s(6),
                height: s(6),
                borderRadius: 3,
                background: i === slide ? accent : '#C5D5E8',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s',
                padding: 0,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};
